import { COMMA, ENTER } from '@angular/cdk/keycodes';
import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ViewChild,
  OnChanges,
  SimpleChanges,
} from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import {
  MatLegacyChipInputEvent as MatChipInputEvent,
  MatLegacyChipList as MatChipList,
} from '@angular/material/legacy-chips';

import { LumSnackBar } from 'v2/shared/lum-snack-bar/lum-snack-bar.service';

@Component({
  selector: 'filter-multi-input', // eslint-disable-line @angular-eslint/component-selector
  templateUrl: './filter-multi-input.component.html',
  styleUrls: ['./filter-multi-input.component.scss'],
})
export class FilterMultiInputComponent implements OnInit, OnChanges {
  @Input()
  value :string[] = [];

  @Input()
  label :string;

  @Input()
  placeholder :string = 'Enter a value';

  @Input()
  inputMode :MultiInputFilter['inputMode'] = 'text';

  @Output()
  changeSelection :EventEmitter<string[] | string> = new EventEmitter<string[]>();

  @ViewChild('inputField', { static: false })
  inputField :ElementRef;

  @ViewChild('chipList', { static: false })
  chipList :MatChipList;

  control = new UntypedFormControl();
  items :string[] = [];
  addOnBlur :boolean = true;
  readonly separatorKeysCodes = [ENTER, COMMA] as const;

  constructor(private snackBar :LumSnackBar) {}

  ngOnInit() :void {
    this.setDefaultValue();
    this.onChangeValue();
  }

  ngOnChanges(changes :SimpleChanges) :void {
    if (changes.value) {
      const currentValue = changes.value.currentValue;
      if (typeof currentValue === 'undefined') {
        this.items = [];
      }
    }
  }

  add(event :MatChipInputEvent) :void {
    const value = (event.value || '').trim();

    if (!value) return;

    const errorMsg = this.#invalidReason(value);

    if (errorMsg) {
      this.snackBar.openWarning(errorMsg);

      return;
    }

    this.items.push(value);
    this.control.setValue(this.items);
    this.inputField.nativeElement.value = '';
  }

  remove(item :string) :void {
    const index = this.items.indexOf(item);

    if (index >= 0) {
      this.items.splice(index, 1);
      this.control.setValue(this.items);
    }
  }

  /**
   * Returns a validation message if the value is invalid for the current input mode,
   * or null if the value is valid
   * @param value The value to validate
   * @returns string | null - Validation message if invalid, null if valid
   */
  #invalidReason(value :string) :string | null {
    if (!value) {
      return null;
    }

    switch (this.inputMode) {
      case 'numeric':
        return /^-?\d+$/.test(value)
          ? null
          : 'Only numbers are allowed for this filter (no decimals or letters)';
      case 'decimal':
        return /^-?(\d*\.)?\d+$/.test(value)
          ? null
          : 'Only decimal numbers are allowed for this filter';
      case 'text':
      case 'tel':
      default:
        return null;
    }
  }

  private setDefaultValue() :void {
    if (this.value) {
      if (Array.isArray(this.value)) {
        // Only add values that don't have validation errors
        this.value
          .filter((item) => !this.#invalidReason(item))
          .forEach((item) => this.items.push(item));
      } else {
        const splitItems = (String(this.value) || '').split(',');
        splitItems
          .filter((item) => !this.#invalidReason(item.trim()))
          .forEach((item) => this.items.push(item.trim()));
      }
    }

    this.control.setValue(this.value);
  }

  private onChangeValue() :void {
    this.control.valueChanges.subscribe((value) => {
      this.changeSelection.emit(value);
    });
  }
}
